import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { promoteReportTemplate } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clinicScopeValue, groupScopeValue, parseScopeValue, } from './useTemplateScopes';
/**
 * Copies a personal template into a clinic (enterprise) or TC group scope via
 * POST /reports/templates/{id}/promote. The original personal row is kept.
 */
export function PromoteTemplateDialog({ template, scopeOptions, onClose, }) {
    const { t } = useTranslation('settings');
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const defaultTarget = scopeOptions.clinics[0]
        ? clinicScopeValue(scopeOptions.clinics[0].clinicId)
        : scopeOptions.groups[0]
            ? groupScopeValue(scopeOptions.groups[0].id)
            : '';
    const [targetValue, setTargetValue] = useState(defaultTarget);
    const [isPromoting, setIsPromoting] = useState(false);
    const handlePromote = async () => {
        const target = parseScopeValue(targetValue);
        if (target.scope === 'personal')
            return;
        setIsPromoting(true);
        try {
            await promoteReportTemplate(template.id, {
                scope: target.scope,
                clinicId: target.clinicId,
                groupId: target.groupId,
            });
            toast({ title: t('reporting.promoteSuccess') });
            void queryClient.invalidateQueries({ queryKey: ['report-templates'] });
            onClose();
        }
        catch {
            toast({ title: t('reporting.promoteFailed'), variant: 'destructive' });
            setIsPromoting(false);
        }
    };
    return (_jsx(Dialog, { open: true, onOpenChange: (open) => !open && onClose(), children: _jsxs(DialogContent, { className: "max-w-sm", "aria-describedby": undefined, children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: t('reporting.promoteTitle') }) }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('reporting.promoteDesc', { name: template.name }) }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { htmlFor: "promote-target", children: t('reporting.promoteTarget') }), _jsxs(Select, { value: targetValue, onValueChange: setTargetValue, children: [_jsx(SelectTrigger, { id: "promote-target", className: "h-8 text-xs", children: _jsx(SelectValue, {}) }), _jsxs(SelectContent, { children: [scopeOptions.clinics.map((c) => (_jsx(SelectItem, { value: clinicScopeValue(c.clinicId), className: "text-xs", children: t('reporting.templateEditor.scopeClinic', { name: c.clinicName }) }, c.clinicId))), scopeOptions.groups.map((g) => (_jsx(SelectItem, { value: groupScopeValue(g.id), className: "text-xs", children: t('reporting.templateEditor.scopeGroup', { name: g.name }) }, g.id)))] })] })] }), _jsxs(DialogFooter, { children: [_jsx(Button, { variant: "outline", size: "sm", onClick: onClose, children: t('button.cancel', { ns: 'common' }) }), _jsx(Button, { size: "sm", disabled: !targetValue || isPromoting, onClick: handlePromote, children: t('reporting.promote') })] })] }) }));
}
